import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { loadStripe } from '@stripe/stripe-js';
import {
    Elements,
    PaymentElement,
    useStripe,
    useElements
} from '@stripe/react-stripe-js';
import './Checkout.css';
import './LojaCheckout.css';
import API_BASE_URL from '../config/api';

const STRIPE_PK = 'pk_live_51TGcfPKmhnqdWeH5Q4hXyWUSydkJdPMJwDd4PR1DsAW2hSlDYJmstsZLprRCyUIxd6a0j3uLLTDNhkMfC1BEw3Fk002NxTskSi'
const stripePromise = loadStripe(STRIPE_PK);

const getImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith('http')) return imageUrl;
    return `${API_BASE_URL}${imageUrl.startsWith('/') ? imageUrl : '/' + imageUrl}`;
};

const stripEmailAlias = (email) => {
    if (!email) return '';
    const parts = email.split('@');
    if (parts.length !== 2) return email;
    const local = parts[0].split('+')[0];
    return `${local}@${parts[1]}`;
};


function LojaCheckoutForm({
    amount,
    items,
    clearCart,
    guestName,
    guestEmail,
    setGuestName,
    setGuestEmail,
    isMember,
    onCancel
}) {
    const stripe = useStripe();
    const elements = useElements();
    const navigate = useNavigate();

    const [notes, setNotes] = useState('');

    const [isProcessing, setIsProcessing] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [isStripeReady, setIsStripeReady] = useState(false);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);

    // Member contact edit state
    const [isEditingContact, setIsEditingContact] = useState(false);
    const [editName, setEditName] = useState(localStorage.getItem('userName') || '');
    const [editEmail, setEditEmail] = useState(stripEmailAlias(localStorage.getItem('userEmail') || ''));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements) return;

        // Basic client validation
        if (!isMember && (!guestName.trim() || !guestEmail.trim())) {
            setErrorMessage('Por favor, preencha o seu nome e email.');
            return;
        }

        setIsProcessing(true);
        setErrorMessage(null);

        // 1. Submit stripe payment element first (triggers client-side validations)
        const { error: submitError } = await elements.submit({
            billing_details: {
                name: isMember ? editName : guestName,
                email: isMember ? editEmail : guestEmail,
            }
        });
        if (submitError) {
            setErrorMessage(submitError.message);
            setIsProcessing(false);
            return;
        }

        try {
            const token = localStorage.getItem('token');

            // 2. Build the order creation payload
            const payload = {
                items: items.map(item => ({
                    variantId: item.variantId,
                    quantity: item.quantity,
                    customName: item.customName || null,
                    customNumber: item.customNumber ? item.customNumber.toString() : null,
                    variantDescription: item.variantDescription || null,
                    athleteProfileId: item.athleteProfileId || null,
                    teamId: item.teamId || null,
                    kitPieceSelections: (item.kitPieceSelections || []).map(kp => ({
                        kitPieceId: kp.kitPieceId,
                        pieceName: kp.pieceName || `Peça ${kp.kitPieceId}`,
                        unitIndex: kp.unitIndex,
                        selectedSize: kp.selectedSize,
                        customName: kp.customName || null,
                        customNumber: kp.customNumber ? kp.customNumber.toString() : null
                    }))
                })),
                // For members, send edited name/email as override (backend will prefer these over DB values)
                guestEmail: isMember ? (editEmail || null) : guestEmail,
                guestName: isMember ? (editName || null) : guestName,
                shippingAddress: null,
                notes: notes.trim() || null
            };

            const headers = {
                'Content-Type': 'application/json'
            };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            // Create order in backend
            const res = await fetch(`${API_BASE_URL}/api/store/orders`, {
                method: 'POST',
                headers,
                body: JSON.stringify(payload)
            });

            if (!res.ok) {
                const errorData = await res.json();
                throw new Error(errorData.message || 'Erro ao processar encomenda no servidor.');
            }

            const data = await res.json();
            const clientSecret = data.clientSecret;

            // 3. Confirm payment with Stripe. Redirect user to success landing page.
            const { error: confirmError } = await stripe.confirmPayment({
                elements,
                clientSecret,
                confirmParams: {
                    return_url: `${window.location.origin}/payment-success?type=store&orderId=${data.orderId}`,
                },
            });

            if (confirmError) {
                setErrorMessage(confirmError.message);
                setIsProcessing(false);
            }
        } catch (err) {
            setErrorMessage(err.message || 'Ocorreu um erro ao processar o pagamento.');
            setIsProcessing(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="store-checkout-form">
            {/* Contact Information */}
            <section className="checkout-section">
                <h3 className="section-title">
                    <span className="section-step">1</span>
                    <i className="fas fa-user-circle" /> Informações de Contacto
                </h3>
                {isMember ? (
                    isEditingContact ? (
                        <div className="member-edit-form">
                            <div className="form-grid">
                                <div className="form-group">
                                    <label htmlFor="editName">Nome Completo</label>
                                    <input
                                        id="editName"
                                        type="text"
                                        value={editName}
                                        onChange={(e) => setEditName(e.target.value)}
                                        placeholder="Nome completo"
                                        autoFocus
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="editEmail">Endereço de Email</label>
                                    <input
                                        id="editEmail"
                                        type="email"
                                        value={editEmail}
                                        onChange={(e) => setEditEmail(e.target.value)}
                                        placeholder="exemplo@email.com"
                                    />
                                </div>
                            </div>
                            <div className="member-edit-actions">
                                <button
                                    type="button"
                                    className="member-edit-cancel"
                                    onClick={() => {
                                        setEditName(localStorage.getItem('userName') || '');
                                        setEditEmail(stripEmailAlias(localStorage.getItem('userEmail') || ''));
                                        setIsEditingContact(false);
                                    }}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="button"
                                    className="member-edit-confirm"
                                    onClick={() => setIsEditingContact(false)}
                                >
                                    <i className="fas fa-check" /> Confirmar
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="member-badge-info">
                            <div className="member-badge-header">
                                <div className="badge-pill">Sócio Autenticado</div>
                                <button
                                    type="button"
                                    className="member-edit-btn"
                                    onClick={() => setIsEditingContact(true)}
                                    title="Editar dados de contacto"
                                >
                                    <i className="fas fa-pencil-alt" /> Editar
                                </button>
                            </div>
                            <p className="member-name-label"><strong>Nome:</strong> {editName || localStorage.getItem('userName')}</p>
                            <p className="member-email-label"><strong>Email:</strong> {editEmail || stripEmailAlias(localStorage.getItem('userEmail'))}</p>
                        </div>
                    )
                ) : (
                    <div className="form-grid">
                        <div className="form-group">
                            <label htmlFor="guestName">Nome Completo</label>
                            <input
                                id="guestName"
                                type="text"
                                value={guestName}
                                onChange={(e) => setGuestName(e.target.value)}
                                placeholder="Insira o seu nome"
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="guestEmail">Endereço de Email</label>
                            <input
                                id="guestEmail"
                                type="email"
                                value={guestEmail}
                                onChange={(e) => setGuestEmail(e.target.value)}
                                placeholder="exemplo@email.com"
                                required
                            />
                        </div>
                    </div>
                )}
            </section>

            {/* Pickup Notice */}
            <section className="checkout-section">
                <h3 className="section-title">
                    <span className="section-step">2</span>
                    <i className="fas fa-store" /> Levantamento no Clube
                </h3>

                <div className="pickup-notice-card">
                    <div className="pickup-notice-header">
                        <div className="pickup-icon-bubble">
                            <i className="fas fa-map-marker-alt" />
                        </div>
                        <div>
                            <h4 className="pickup-notice-title">Levantamento Presencial no Clube</h4>
                            <span className="pickup-notice-badge">Sem custos de envio</span>
                        </div>
                    </div>
                    <p className="pickup-notice-desc">
                        As encomendas da loja oficial não são enviadas por correio. Os artigos devem ser levantados presencialmente na <strong>secretaria do Clube Desportivo da Póvoa</strong>.
                    </p>
                    <div className="pickup-info-list">
                        <div className="pickup-info-item">
                            <i className="fas fa-building" />
                            <span><strong>Local:</strong> Secretaria do CDP (Pavilhão Linhares de Castro)</span>
                        </div>
                        <div className="pickup-info-item">
                            <i className="fas fa-envelope-open-text" />
                            <span><strong>Aviso:</strong> Receberá uma notificação por email assim que a sua encomenda estiver pronta para levantamento.</span>
                        </div>
                    </div>
                </div>

                <div className="form-group" style={{ marginTop: '16px' }}>
                    <label htmlFor="notes">Observações (Opcional)</label>
                    <textarea
                        id="notes"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Alguma indicação ou detalhe adicional..."
                        rows="2"
                    />
                </div>
            </section>

            {/* Payment Section */}
            <section className="checkout-section checkout-section--full">
                <h3 className="section-title">
                    <span className="section-step">3</span>
                    <i className="fas fa-credit-card" /> Informações de Pagamento
                </h3>

                {/* Klarna BNPL info badge */}
                <div className="klarna-info-badge">
                    <div className="klarna-badge-icon">
                        <svg viewBox="0 0 50 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Klarna">
                            <rect width="50" height="20" rx="4" fill="#FFB3C7" />
                            <text x="50%" y="14" textAnchor="middle" fill="#17120E" fontSize="10" fontWeight="700" fontFamily="Arial,sans-serif">klarna</text>
                        </svg>
                    </div>
                    <div className="klarna-badge-text">
                        <strong>Pague a prestações com Klarna</strong>
                        <span>Divida o pagamento em 3 prestações sem juros.</span>
                    </div>
                </div>

                <div className="stripe-element-container">
                    <PaymentElement
                        id="payment-element"
                        onReady={() => setIsStripeReady(true)}
                        onChange={(e) => setSelectedPaymentMethod(e.value?.type || null)}
                        options={{
                            layout: {
                                type: 'accordion',
                                defaultCollapsed: false,
                                radios: false,
                                spacedAccordionItems: true,
                            },
                            defaultValues: {
                                billingDetails: {
                                    name: isMember ? (localStorage.getItem('userName') || '') : guestName,
                                    email: isMember ? (localStorage.getItem('userEmail') || '') : guestEmail,
                                }
                            }
                        }}
                    />
                </div>
            </section>

            {errorMessage && (
                <div className="co-error-banner" role="alert">
                    <i className="fas fa-exclamation-triangle" />
                    <span>{errorMessage}</span>
                </div>
            )}

            <div className="co-actions">
                <button
                    type="button"
                    className="co-btn-cancel"
                    onClick={onCancel}
                    disabled={isProcessing}
                >
                    Voltar à Loja
                </button>
                <button
                    id="checkout-submit-btn"
                    type="submit"
                    className="co-btn-pay"
                    disabled={!stripe || !elements || isProcessing || !isStripeReady}
                >
                    {isProcessing ? (
                        <>
                            <span className="co-spinner" />
                            A processar...
                        </>
                    ) : selectedPaymentMethod === 'klarna' ? (
                        <>
                            <i className="fas fa-arrow-right" />
                            Continuar para Klarna &middot; {(amount / 3).toFixed(2)}€ × 3
                        </>
                    ) : (
                        <>
                            <i className="fas fa-lock" />
                            Pagar {amount.toFixed(2)}€
                        </>
                    )}
                </button>
            </div>
        </form>
    );
}

// ─── Main Checkout Container Component ────────────────────────────────────────
export default function LojaCheckout() {
    const { items, totalPrice, clearCart } = useCart();
    const navigate = useNavigate();

    const [guestName, setGuestName] = useState('');
    const [guestEmail, setGuestEmail] = useState('');

    const isMember = !!localStorage.getItem('token');

    // Fill member fields from localStorage
    useEffect(() => {
        if (isMember) {
            setGuestName(localStorage.getItem('userName') || '');
            setGuestEmail(localStorage.getItem('userEmail') || '');
        }
    }, [isMember]);

    // Compute dynamic item prices and final amount depending on member status
    const getItemPrice = useCallback((item) => {
        return isMember && item.memberPrice !== null && item.memberPrice !== undefined
            ? item.memberPrice
            : item.basePrice;
    }, [isMember]);

    const totalAmount = useMemo(() => {
        return items.reduce((sum, item) => sum + getItemPrice(item) * item.quantity, 0);
    }, [items, getItemPrice]);

    // Redirect to store page if the cart becomes empty
    useEffect(() => {
        if (items.length === 0) {
            const timeout = setTimeout(() => {
                navigate('/loja', { replace: true });
            }, 3000);
            return () => clearTimeout(timeout);
        }
    }, [items, navigate]);

    const handleCancel = () => {
        navigate('/loja');
    };

    const stripeElementsOptions = useMemo(() => {
        if (totalAmount <= 0) return null;
        return {
            mode: 'payment',
            amount: Math.round(totalAmount * 100),
            currency: 'eur',
            appearance: {
                theme: 'stripe',
                variables: {
                    colorPrimary: '#003380',
                    colorBackground: '#ffffff',
                    colorText: '#0f172a',
                    colorDanger: '#dc2626',
                    fontFamily: '"Barlow", system-ui, sans-serif',
                    borderRadius: '6px',
                }
            },
            locale: 'pt',
            defaultValues: {
                billingDetails: {
                    email: guestEmail || undefined,
                    name: guestName || undefined,
                }
            }
        };
    }, [totalAmount, guestEmail, guestName]);

    if (items.length === 0) {
        return (
            <div className="checkout-empty-container">
                <div className="checkout-empty-card">
                    <i className="fas fa-shopping-cart empty-icon" />
                    <h2>O seu carrinho está vazio</h2>
                    <p>Adicione artigos na loja para poder efetuar a compra.</p>
                    <button className="btn btn-primary-blue" onClick={handleCancel}>
                        Ir para a Loja
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="co-page">
            {/* Sidebar: Cart items and Subtotal */}
            <aside className="co-sidebar">
                <div className="co-sidebar-inner">
                    <div className="co-brand">
                        <img src="/CDP_logo.png" alt="CDP Logo" className="co-logo" />
                        <div className="co-brand-text">
                            <span className="co-club-name">Clube Desportivo da Póvoa</span>
                            <span className="co-club-sub">Loja Oficial</span>
                        </div>
                    </div>

                    <div className="co-divider" />

                    <div className="co-order-summary">
                        <p className="co-order-label">Produtos na encomenda</p>

                        <div className="co-profiles-list store-items-checkout-list">
                            {items.map((item, idx) => {
                                const unitPrice = getItemPrice(item);
                                return (
                                    <div key={item.cartItemId || item.variantId || idx} className="co-profile-row store-item-row">
                                        <div className="store-item-image-wrapper">
                                            {item.imageUrl ? (
                                                <img src={getImageUrl(item.imageUrl)} alt={item.productName} className="store-item-img-mini" />
                                            ) : (
                                                <div className="store-item-placeholder-mini"><i className="fas fa-tshirt" /></div>
                                            )}
                                        </div>
                                        <div className="store-item-details">
                                            <span className="co-profile-name">{item.productName}</span>
                                            <span className="co-profile-sub">
                                                Quant: {item.quantity}
                                                {item.variantDescription && ` · ${item.variantDescription}`}
                                                {!item.variantDescription && item.size && ` · Tam: ${item.size}`}
                                                {!item.variantDescription && item.color && ` · Cor: ${item.color}`}
                                                {!item.kitPieceSelections && (item.customName || item.customNumber) && ` · Personalização: ${item.customName || '-'} / ${item.customNumber ? `Nº ${item.customNumber}` : '-'}`}
                                            </span>
                                            {item.kitPieceSelections && item.kitPieceSelections.length > 0 && (
                                                <div style={{
                                                    marginTop: '10px',
                                                    padding: '12px',
                                                    backgroundColor: '#f8fafc',
                                                    borderRadius: '8px',
                                                    fontSize: '11.5px',
                                                    border: '1px solid #e2e8f0',
                                                    boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                                                }}>
                                                    <strong style={{ color: '#334155', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                                        <i className="fas fa-layer-group" style={{ color: 'var(--color-primary, #38bdf8)' }}></i>
                                                        Composição do Kit
                                                    </strong>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                        {item.kitPieceSelections.map((kp, kIdx) => (
                                                            <div key={kIdx} style={{
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                paddingBottom: kIdx < item.kitPieceSelections.length - 1 ? '8px' : '0',
                                                                borderBottom: kIdx < item.kitPieceSelections.length - 1 ? '1px solid #e2e8f0' : 'none'
                                                            }}>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                                                                    <span style={{ color: '#0f172a', fontWeight: '600' }}>{kp.quantity || 1}x {kp.pieceName}</span>
                                                                    <span style={{ color: '#475569', fontWeight: '500', whiteSpace: 'nowrap' }}>
                                                                        Tam: <strong style={{ color: '#0f172a' }}>{kp.selectedSize}</strong>
                                                                    </span>
                                                                </div>
                                                                {(kp.customName || kp.customNumber) ? (
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#64748b', marginTop: '3px' }}>
                                                                        <i className="fas fa-pen" style={{ fontSize: '9px', opacity: 0.7 }}></i>
                                                                        <span>
                                                                            {kp.customName ? kp.customName : ''}
                                                                            {kp.customName && kp.customNumber ? ' / ' : ''}
                                                                            {kp.customNumber ? `Nº ${kp.customNumber}` : ''}
                                                                        </span>
                                                                    </div>
                                                                ) : null}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        <span className="co-profile-price">
                                            {(unitPrice * item.quantity).toFixed(2)}€
                                        </span>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="co-divider" />

                        <div className="co-total-row">
                            <span>Total</span>
                            <span className="co-total-amount">{totalAmount.toFixed(2)}€</span>
                        </div>
                    </div>

                    <div className="co-security-badges">
                        <div className="co-badge">
                            <i className="fas fa-lock" />
                            <span>Pagamento 100% Seguro</span>
                        </div>
                        <div className="co-badge">
                            <i className="fab fa-stripe" />
                            <span>Processado pelo Stripe</span>
                        </div>
                    </div>
                </div>
            </aside>

            {/* Main Form container */}
            <main className="co-main">
                <div className="co-main-inner">
                    <button className="co-back-btn" onClick={handleCancel}>
                        <i className="fas fa-arrow-left" />
                        Voltar para a Loja
                    </button>

                    <h1 className="co-title">Finalizar Encomenda</h1>
                    <p className="co-subtitle">Confirme os dados e selecione o método de pagamento</p>

                    {stripeElementsOptions ? (
                        <Elements stripe={stripePromise} options={stripeElementsOptions}>
                            <LojaCheckoutForm
                                amount={totalAmount}
                                items={items}
                                clearCart={clearCart}
                                guestName={guestName}
                                guestEmail={guestEmail}
                                setGuestName={setGuestName}
                                setGuestEmail={setGuestEmail}
                                isMember={isMember}
                                onCancel={handleCancel}
                            />
                        </Elements>
                    ) : (
                        <div className="co-loading">
                            <div className="co-loading-spinner" />
                            <p>A inicializar o checkout seguro...</p>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}
